import { defineStore } from "pinia";
import axios from "@/app/plugins/axios";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import i18n from "@/app/plugins/i18n";
import { useAccountStore } from "@/account/stores/account";
import { useProductsStore } from "@/catalog/stores/products";
import default_product_image from "@/shared/assets/default_product_image.png";
import { getErrorMessage } from "@/shared/utils/errorMessage";

export interface ProductTag {
  id: string;
  name: string;
  color?: string; // Keep for backward compatibility
  hexColor: string;
  images?: string[];
  active: boolean;
  created: number;
  updated: number;
  metadata?: Record<string, string>;
}

export type ProductTagFilterValue = string | number | boolean | null | string[];
export type ProductTagFilterCondition = Record<string, ProductTagFilterValue>;

export interface ProductTagsStore {
  productTags: ProductTag[];
  selectedProductTag: ProductTag | null;
  loading: boolean;
  filterProductTagCondition: ProductTagFilterCondition;
  listLoading: boolean;
  exporting: boolean;
  noResults: boolean;
  hasMore: boolean;
  hasNext: boolean;
  hasPrev: boolean;
  nextPage: string;
}

export interface ProductTagSearchPagination {
  limit?: number;
  nextPage?: string;
  page?: number;
}

export interface ProductTagSearchOptions {
  pagination?: ProductTagSearchPagination;
  filters?: ProductTagFilterCondition;
  includeStoredFilters?: boolean;
  updateStore?: boolean;
}

export interface ProductTagSearchResponse {
  data: ProductTag[];
  hasNext?: boolean;
  hasPrev?: boolean;
}

export type ProductTagPayloadValue =
  | string
  | number
  | boolean
  | null
  | string[]
  | Record<string, string>;
export type ProductTagPayload = Record<string, ProductTagPayloadValue>;

export const useProductTagsStore = defineStore("productTags", {
  state: (): ProductTagsStore => ({
    productTags: [],
    selectedProductTag: null,
    loading: false,
    filterProductTagCondition: {},
    listLoading: false,
    exporting: false,
    noResults: false,
    hasMore: false,
    hasNext: false,
    hasPrev: false,
    nextPage: "",
  }),

  getters: {
    isLoading: (state) => state.loading,
    isListLoading: (state) => state.listLoading,
    isExporting: (state) => state.exporting,
    getProductTags: (state) => state.productTags,
    getSelectedProductTag: (state) => state.selectedProductTag,
  },

  actions: {
    setSelectedProductTag(productTag: ProductTag): void {
      this.selectedProductTag = productTag;
    },

    setSelectedProductTagNull(): void {
      this.selectedProductTag = null;
    },

    setFilterProductTagCondition(condition: ProductTagFilterCondition): void {
      this.filterProductTagCondition = condition;
    },

    getProductTagDefaultImage(images?: string[]): string {
      return (
        images?.find((image) => image.includes("default.jpg")) ||
        images?.[0] ||
        default_product_image
      );
    },

    async syncProductTagImages(productTagId: string): Promise<string[] | null> {
      const accountId = useAccountStore().account?.id;
      if (!accountId) {
        return null;
      }

      const uploadedImages = await useProductsStore().uploadImages(
        productTagId,
        accountId,
        "productTags",
      );
      const images = useProductsStore().filterImagesPayload(uploadedImages);
      await axios.patch(`/productTag/${productTagId}`, { images });
      return images;
    },

    async createProductTag(productTagData: ProductTagPayload) {
      try {
        this.loading = true;
        // Filter out empty values before sending the request
        const filteredData = this._filterEmptyValues(productTagData);
        const response = await axios.post<ProductTag>("/productTag", filteredData);
        const productTag = response.data;
        const images = productTag?.id ? await this.syncProductTagImages(productTag.id) : null;
        if (images) {
          response.data = { ...productTag, images };
          this.selectedProductTag = response.data;
        }
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.productTagDetails.productTagCreated"),
        });
        return response;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.loading = false;
      }
    },

    async retrieveProductTag(id: string | string[] | number, doLoading = true): Promise<void> {
      try {
        if (doLoading) this.loading = true;
        const response = await axios.get<ProductTag>(`/productTag/${id}`);
        const productTag = response.data;
        this.selectedProductTag = productTag;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        if (doLoading) this.loading = false;
      }
    },

    async updateProductTag(
      updateData: ProductTagPayload,
      id: string | string[] | number,
      options: { syncImages?: boolean } = {},
    ) {
      try {
        // Filter out empty values before sending the request
        const filteredData = this._filterEmptyValues(updateData);

        // Remove any server-controlled fields if they somehow made it into the payload
        const {
          id: _id,
          created: _created,
          updated: _updated,
          deleted: _deleted,
          color: _color,
          ...cleanedData
        } = filteredData;

        const response = await axios.patch<ProductTag>(`/productTag/${id}`, cleanedData);

        const productTag = response.data;

        const productTagIndex = this.productTags?.findIndex(
          (p: ProductTag) => p.id === productTag.id,
        );
        if (productTagIndex !== -1) {
          this.productTags[productTagIndex] = productTag;
        }

        this.selectedProductTag = response.data;

        const images =
          options.syncImages && productTag?.id
            ? await this.syncProductTagImages(productTag.id)
            : null;
        if (images) {
          const updatedProductTag = { ...productTag, images };
          if (productTagIndex !== -1) {
            this.productTags[productTagIndex] = updatedProductTag;
          }
          this.selectedProductTag = updatedProductTag;
          response.data = updatedProductTag;
        }

        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.productTagDetails.productTagUpdated"),
        });
        return response;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async searchProductTags(
      {
        pagination = {},
        filters = {},
        includeStoredFilters = true,
        updateStore = true,
      }: ProductTagSearchOptions = {},
      initLoading = false,
    ) {
      if (initLoading) this.loading = true;
      else {
        this.listLoading = true;
        if (updateStore) {
          this.productTags = [];
        }
      }

      try {
        const payload = {
          filters: {
            ...(includeStoredFilters ? this.filterProductTagCondition : {}),
            ...filters,
          },
          pagination: {
            limit: 10,
            ...pagination,
          },
        };

        const response = await axios.post<ProductTagSearchResponse>("/productTag/search", payload);
        const result = response.data;

        const productTags = result.data || [];

        if (updateStore) {
          // Set pagination flags directly from the response
          this.hasNext = result.hasNext ?? false;
          this.hasPrev = result.hasPrev ?? false;

          this.noResults = productTags.length === 0;
          this.productTags = productTags;
        }

        return productTags;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return [];
      } finally {
        this.loading = false;
        this.listLoading = false;
      }
    },

    async checkIfProductTagsExist(): Promise<boolean> {
      try {
        const response = await axios.post<ProductTagSearchResponse>("/productTag/search", {
          filters: {},
          pagination: {
            limit: 1,
          },
        });
        return response.data.data.length > 0;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return false;
      }
    },

    async exportProductTags(customFilters = {}): Promise<boolean> {
      try {
        this.exporting = true;
        useSnackBarStore().toastInfo({
          message:
            i18n.global.t("common.action.preparingExport") || "Preparing export, please wait...",
        });

        const payload = {
          filters: {
            ...this.filterProductTagCondition,
            ...customFilters,
          },
          export: true,
        };

        const response = await axios.post("/productTag/export", payload, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let filename = "product-tags-export.csv";

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        useSnackBarStore().toastSuccess({
          message:
            i18n.global.t("common.action.exportProductTagsSuccess") ||
            "Product tags export completed successfully",
        });

        return true;
      } catch (error) {
        useSnackBarStore().toastError({
          message: getErrorMessage(error, "Failed to export product tags data"),
        });
        return false;
      } finally {
        this.exporting = false;
      }
    },

    async searchProductTagOptions({
      pagination = {},
      filters = {},
    }: ProductTagSearchOptions = {}): Promise<ProductTag[]> {
      try {
        const limit = pagination.limit || 1000;
        const productTagOptions: ProductTag[] = [];
        let nextPage = pagination.nextPage;
        let hasNext = true;

        while (hasNext) {
          const payload = {
            filters,
            pagination: {
              limit,
              ...(nextPage ? { nextPage } : {}),
            },
          };

          const response = await axios.post<ProductTagSearchResponse>(
            "/productTag/search",
            payload,
          );
          const pageData = response.data?.data || [];
          productTagOptions.push(...pageData);

          hasNext = response.data?.hasNext === true && pageData.length > 0;
          nextPage = hasNext ? pageData[pageData.length - 1]?.id : undefined;
        }

        return productTagOptions;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return [];
      }
    },

    async previewBulkStatusUpdate(
      id: string,
      payload: { target: "products" | "productClasses"; active: boolean },
    ): Promise<{
      target: "products" | "productClasses";
      active: boolean;
      matchingCount: number;
      changedCount: number;
    } | null> {
      try {
        const response = await axios.post<{
          target: "products" | "productClasses";
          active: boolean;
          matchingCount: number;
          changedCount: number;
        }>(`/productTag/${id}/bulkStatus/preview`, payload);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return null;
      }
    },

    async bulkStatusUpdate(
      id: string,
      payload: { target: "products" | "productClasses"; active: boolean },
    ): Promise<{
      success: boolean;
      target: "products" | "productClasses";
      active: boolean;
      matchingCount: number;
      changedCount: number;
    } | null> {
      try {
        this.loading = true;
        const response = await axios.post<{
          success: boolean;
          target: "products" | "productClasses";
          active: boolean;
          matchingCount: number;
          changedCount: number;
        }>(`/productTag/${id}/bulkStatus`, payload);
        const result = response.data;
        const targetLabel =
          payload.target === "products"
            ? i18n.global.t("common.action.products")
            : i18n.global.t("common.action.productClasses");

        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.productTagDetails.bulkStatusSuccess", {
            count: result.changedCount,
            target: targetLabel,
            action: payload.active
              ? i18n.global.t("common.action.activate").toString().toLowerCase()
              : i18n.global.t("common.action.deactivate").toString().toLowerCase(),
          }),
        });
        return result;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return null;
      } finally {
        this.loading = false;
      }
    },

    async removeFromAllProducts(id: string): Promise<boolean> {
      try {
        this.loading = true;
        await axios.post(`/productTag/${id}/removeFromProducts`);

        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.productTagDetails.tagRemovedFromAllProducts"),
        });

        return true;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return false;
      } finally {
        this.loading = false;
      }
    },

    async removeFromAllProductClasses(id: string): Promise<boolean> {
      try {
        this.loading = true;
        await axios.post(`/productTag/${id}/removeFromProductClasses`);

        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.productTagDetails.tagRemovedFromAllProductClasses"),
        });

        return true;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return false;
      } finally {
        this.loading = false;
      }
    },

    // Helper method to filter out empty values
    _filterEmptyValues(data: ProductTagPayload): ProductTagPayload {
      return Object.entries(data)
        .filter(([_, value]) => {
          // Consider empty strings, null, and undefined as empty
          if (value === "" || value === null || value === undefined) {
            return false;
          }

          // For arrays, check if they have at least one value
          if (Array.isArray(value) && value.length === 0) {
            return false;
          }

          // For objects, check if they have at least one key
          if (
            typeof value === "object" &&
            !Array.isArray(value) &&
            value !== null &&
            Object.keys(value).length === 0
          ) {
            return false;
          }

          return true;
        })
        .reduce<ProductTagPayload>((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});
    },
  },
});
